import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Rating,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { ArrowBack as BackIcon, Star as StarIcon } from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { useParams, useNavigate } from 'react-router-dom';
import { peerReviewService, PeerReviewAssignment } from '../../services/peerReviewService';

const PeerReviewResult: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState<PeerReviewAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadAssignment();
    }
  }, [id]);

  const loadAssignment = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await peerReviewService.getAssignment(parseInt(id!));
      setAssignment(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading assignment');
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageRating = () => {
    if (!assignment?.reviews || assignment.reviews.length === 0) return null;
    const sum = assignment.reviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / assignment.reviews.length).toFixed(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assignment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('peerReview.notFound', 'Auftrag nicht gefunden')}</Alert>
      </Box>
    );
  }

  const avgRating = calculateAverageRating();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/peer-review')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5">
          {t('peerReview.resultTitle', 'Feedback für: %{lesson}', { lesson: assignment.lessonTitle })}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Your submission */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('peerReview.yourSubmission', 'Ihre Abgabe')}
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
          }}
        >
          {assignment.submission}
        </Box>
      </Paper>

      {/* Average rating */}
      {avgRating && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('peerReview.averageRating', 'Durchschnittliche Bewertung')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ fontSize: 48, color: 'warning.main' }} />
            <Typography variant="h3">{avgRating}</Typography>
            <Typography variant="h5" color="text.secondary">/ 5</Typography>
          </Box>
          <Chip
            label={t('peerReview.reviewCount', '%{count} Reviews', { count: assignment.reviews?.length || 0 })}
            sx={{ mt: 1 }}
          />
        </Paper>
      )}

      {/* Individual reviews */}
      <Typography variant="h6" gutterBottom>
        {t('peerReview.receivedFeedback', 'Erhaltenes Feedback')}
      </Typography>

      {!assignment.reviews || assignment.reviews.length === 0 ? (
        <Alert severity="info">
          {t('peerReview.noFeedbackYet', 'Noch kein Feedback erhalten. Ihre Abgabe wird noch bearbeitet.')}
        </Alert>
      ) : (
        assignment.reviews.map((review, index) => (
          <Card key={review.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('peerReview.reviewer', 'Reviewer %{number}', { number: index + 1 })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={review.rating} readOnly size="small" />
                  <Typography variant="body2" color="text.secondary">
                    ({review.rating}/5)
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {review.feedback}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {t('peerReview.reviewedOn', 'Bewertet am %{date}', {
                  date: new Date(review.createdAt).toLocaleDateString('de-DE'),
                })}
              </Typography>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
};

export default PeerReviewResult;
